import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Button } from '@astrid/components'
import clsx from 'clsx'
import Folder from 'components/Folder/Folder'
import { ReactComponent as ChevronLeft } from 'resources/graphics/icons/chevron-west.svg'
import { ReactComponent as ChevronRight } from 'resources/graphics/icons/chevron-east.svg'
import { SimpleWordScore, StudySet } from 'store/services/Exercise'
import ScoreIndicator from 'components/ScoreIndicator/ScoreIndicator'

import styles from './StudySetsCarousel.module.scss'

interface StudySetsCarouselProps {
  studySets: StudySet[]
  onStudySetClick: (id: string) => void
}

const StudySetsCarousel = ({ studySets, onStudySetClick }: StudySetsCarouselProps) => {
  const [canScroll, setCanScroll] = useState({
    left: false,
    right: false
  })

  const carouselRef = useRef<HTMLDivElement>(null)
  const scrollableContainerRef = useRef<HTMLDivElement>(null)
  const ticking = useRef(false)

  const updateScrollState = useCallback(() => {
    const scrollableContainer = scrollableContainerRef.current
    if (!scrollableContainer) return

    const { scrollLeft, scrollWidth, clientWidth } = scrollableContainer

    setCanScroll((prev) => {
      const next = {
        left: scrollLeft > 0,
        right: Math.ceil(scrollLeft + clientWidth) < scrollWidth
      }

      if (prev.left === next.left && prev.right === next.right) {
        return prev
      }

      return next
    })
  }, [])

  const onScroll = () => {
    if (ticking.current) return

    ticking.current = true

    requestAnimationFrame(() => {
      updateScrollState()
      ticking.current = false
    })
  }

  useLayoutEffect(() => {
    updateScrollState()
  }, [studySets, updateScrollState])

  useEffect(() => {
    const container = scrollableContainerRef.current
    if (!container) return

    const observer = new ResizeObserver(() => {
      updateScrollState()
    })

    observer.observe(container)

    return () => observer.disconnect()
  }, [updateScrollState])

  const scrollBy = (direction: 'left' | 'right') => {
    const scrollableContainer = scrollableContainerRef.current
    if (!scrollableContainer) return

    const scrollAmount = scrollableContainer.clientWidth * 0.6

    scrollableContainer.scrollBy({
      behavior: 'smooth',
      left: direction === 'left' ? -scrollAmount : scrollAmount
    })
  }

  const renderWordsInColumn = (wordsToRender: SimpleWordScore[], renderCount?: number) => {
    const words = renderCount ? wordsToRender.slice(0, renderCount) : wordsToRender

    return words.map((word, index) => (
      <div className={styles.rowWrapper} key={`${word.word}${index}`}>
        <div className={clsx(styles.word, word.word.length > 8 ? styles.long : styles.short)}>{word.word}</div>

        <ScoreIndicator
          value={word.score}
          sizeInPx={42}
          strokeWidthInPx={7}
          disableAnimation
          fontSize={word.score === 100 ? '13px' : '16px'}
          colorsThresholds={{
            LOW: 50,
            HIGH: 80
          }}
        />
      </div>
    ))
  }

  const renderStudySetCarouselItem = (item: StudySet) => {
    return (
      <button
        type="button"
        aria-label="Open study set"
        key={item._id}
        className={styles.clickable}
        onClick={() => onStudySetClick(item._id)}
      >
        <Folder title={item.name} color="mint">
          <div className={styles.helperContainer}>
            {renderWordsInColumn(item.words, 2)}

            <div
              className={styles.badge}
              style={{
                backgroundColor: '#26d4dd'
              }}
            >
              <span className={styles.badgeText}>
                {item.words.length} word
                {item.words.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </Folder>
      </button>
    )
  }

  return (
    <div className={styles.wrapper} ref={carouselRef}>
      {canScroll.left && (
        <Button
          tabIndex={-1}
          aria-label="button-scroll-study-sets-left"
          className={clsx(styles['control-button'], styles.left)}
          onClick={(event) => {
            event.stopPropagation()
            scrollBy('left')
          }}
        >
          <ChevronLeft />
        </Button>
      )}

      <div className={styles.carouselContainer} ref={scrollableContainerRef} onScroll={onScroll}>
        {studySets.map(renderStudySetCarouselItem)}
      </div>

      {canScroll.right && (
        <Button
          tabIndex={-1}
          aria-label="button-scroll-study-sets-right"
          className={clsx(styles['control-button'], styles.right)}
          onClick={(event) => {
            event.stopPropagation()
            scrollBy('right')
          }}
        >
          <ChevronRight />
        </Button>
      )}
    </div>
  )
}

export default StudySetsCarousel
